/**
 * `wayai knowledge refresh-preview`: the knowledge base's preview made to hold its production
 * base's files (#5142 step 4; K10, K18; #5083 decisions 112 and 145).
 *
 * Files are data, written where agents read them, and a preview is cloned with its config, never
 * its files, so a preview holds production's knowledge only once this runs: after the preview is
 * created, and whenever an operator wants it current again (K10). Nothing runs it on its own — not a
 * hub preview's creation, since every hub preview shares the one preview base and a copy would
 * overwrite the others' edits.
 *
 * It pages the data product's preview file snapshot (`POST /v1/{preview}/files/snapshot`, through
 * the `/api/data` proxy) to its end. Each page makes one range of the preview's files equal
 * production's, removing the preview's own files production does not hold, and the run prints
 * what each did. Every file type production holds is covered, and the preview must hold each one
 * too (the data product refuses the run otherwise). A re-run is safe: a page that finds the
 * preview equal writes, and costs, nothing.
 */

import {
  FILE_SNAPSHOT_CONFLICT_STATUS,
  fileSnapshotChanged,
  runFileSnapshot,
  type FileSnapshotRun,
} from '@wayai/core/contracts';
import { ApiError } from '../lib/api-client.js';
import { expected } from '../lib/expected.js';
import { sanitizeTerminalText as safe } from '../lib/terminal-output.js';
import type { BaseRecord } from '../data/config-as-code/api.js';

/** A base as a refresh reads it: the record, and whether it is deleted. */
export type BaseRow = Pick<BaseRecord, 'id' | 'environment' | 'origin_base_id'> & { deleted?: boolean };

/** The data side a refresh reads: the knowledge base, its preview, and one snapshot page at a time. */
export interface PreviewKnowledgeBase {
  getBase(id: string): Promise<BaseRow | null>;
  listPreviews(id: string): Promise<BaseRow[]>;
  /** One snapshot page's parsed body (`undefined` = the first page). */
  snapshotPage(preview: string, cursor: string | undefined): Promise<unknown>;
}

export interface RefreshOptions {
  orgId: string;
  base: string;
  yes: boolean;
}

/** The command that copies production's files into the knowledge base's preview (#5083 decision 145). */
function refreshPreviewCommand(orgId: string, base: string): string {
  return `wayai knowledge refresh-preview --org ${orgId} --base ${safe(base)}`;
}

/** The knowledge base's production base and its one preview, from `--base` naming either. */
export async function resolveBases(
  kb: Pick<PreviewKnowledgeBase, 'getBase' | 'listPreviews'>,
  baseId: string,
  orgId: string,
): Promise<{ production: string; preview: string }> {
  const base = await kb.getBase(baseId);
  if (!base || base.deleted) throw expected(`Base "${safe(baseId)}" not found in this organization.`);
  if (base.environment === 'preview') {
    const origin = base.origin_base_id ? await kb.getBase(base.origin_base_id) : null;
    if (!origin || origin.deleted || origin.environment !== 'production') {
      throw expected(
        `Base "${safe(baseId)}" is a preview with no production origin, so it has no production files to copy. ` +
          'Name the production knowledge base, or a preview created from it.',
      );
    }
    return { production: origin.id, preview: base.id };
  }
  // The listing can repeat a preview, or still show a deleted one, until its store settles: each
  // candidate is read back from the base itself.
  const candidates = [...new Set((await kb.listPreviews(base.id)).filter((p) => p.origin_base_id === base.id).map((p) => p.id))];
  const previews: string[] = [];
  for (const id of candidates) {
    const preview = await kb.getBase(id);
    if (preview && !preview.deleted && preview.environment === 'preview' && preview.origin_base_id === base.id) previews.push(id);
  }
  if (previews.length !== 1) {
    throw expected(
      previews.length === 0
        ? `Base "${safe(baseId)}" has no preview. Create one with \`wayai bases create-preview ${safe(baseId)} --name <name>\`, ` +
          `then copy production's files into it with \`${refreshPreviewCommand(orgId, baseId)}\`.`
        : `Base "${safe(baseId)}" has ${previews.length} previews (${previews.map(safe).join(', ')}). Pass the one to use as --base.`,
    );
  }
  return { production: base.id, preview: previews[0] };
}

export interface RefreshIo {
  log: (line: string) => void;
  /** Asked before the first page; false cancels. */
  confirm: (question: string) => Promise<boolean>;
}

/** Run one refresh. Returns what it did, or null when cancelled. */
export async function refreshPreviewKnowledge(
  kb: PreviewKnowledgeBase,
  opts: RefreshOptions,
  io: RefreshIo,
): Promise<FileSnapshotRun | null> {
  const { production, preview } = await resolveBases(kb, opts.base, opts.orgId);
  const question =
    `Make the files of preview "${safe(preview)}" equal production "${safe(production)}"'s? ` +
    'Files the preview changed are replaced, and files only it holds are removed.';
  if (!opts.yes && !(await io.confirm(question))) {
    io.log('Cancelled.');
    return null;
  }

  io.log(`Copying the files of production base "${safe(production)}" into its preview "${safe(preview)}".`);
  const run = await runFileSnapshot({
    sendPage: (cursor) => kb.snapshotPage(preview, cursor),
    // The data product refuses a page whose preview file changed while it was being copied, and
    // asks for the same cursor again.
    isConflict: (error) => error instanceof ApiError && error.status === FILE_SNAPSHOT_CONFLICT_STATUS,
    onPage: (page, n) => io.log(`  Page ${n}: ${page.written} written, ${page.removed} removed, ${page.unchanged} unchanged.`),
  });
  io.log(
    !fileSnapshotChanged(run)
      ? `Nothing changed in ${run.pages} page(s): the preview's ${run.unchanged} file(s) already equal production's.`
      : `Done in ${run.pages} page(s): ${run.written} written, ${run.removed} removed, ${run.unchanged} unchanged. ` +
          "The preview's files now equal production's.",
  );
  return run;
}
